/**
 * Connectivity state — navigator reachability + server reachability.
 *
 * WHY THIS IS NOT A `createSlice` REDUCER: `redux/store.ts` is registered by
 * another workstream and cannot be touched from here, so the state lives in a
 * tiny external store (the useSyncExternalStore contract) instead of the
 * redux tree. The shape matches the spec'd slice state 1:1
 * ({ navigatorOnline, serverReachable, degraded, lastCheckedAt }); every
 * mutation notifies subscribers ONLY when a value actually changed, so
 * high-frequency API traffic can't thrash listeners.
 *
 * Consumers:
 *   - hooks/useConnectivity (React binding + /api/health probe loop)
 *   - services/rtkQueryBaseQuery (marks 'up' on success / 4xx, 'down' on
 *     network errors / 5xx)
 *   - hooks/useOffline (mirrors navigator.onLine changes in)
 *   - hooks/usePosOffline.startBackgroundSync (drain gate)
 *
 * `degraded` means: the device's link is up but the server is unreachable —
 * the "sale would be lost if we took the online branch" state this module
 * exists to catch.
 */

export type ServerReachability = 'up' | 'down' | 'unknown';

export interface ConnectivityState {
  /** navigator.onLine — the device's own link state. */
  navigatorOnline: boolean;
  /** Result of the last server probe / API observation. */
  serverReachable: ServerReachability;
  /** Link is up but the server cannot be reached — treat as offline for sale decisions. */
  degraded: boolean;
  /** Epoch ms of the last reachability observation (probe or API response). */
  lastCheckedAt: number | null;
}

const initialState: ConnectivityState = {
  navigatorOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  serverReachable: 'unknown',
  degraded: false,
  lastCheckedAt: null,
};

let state: ConnectivityState = { ...initialState };
const listeners = new Set<() => void>();

function computeDegraded(next: Pick<ConnectivityState, 'navigatorOnline' | 'serverReachable'>): boolean {
  return next.navigatorOnline && next.serverReachable === 'down';
}

function setState(patch: Partial<ConnectivityState>, forceNotify = false): void {
  const merged: ConnectivityState = { ...state, ...patch };
  merged.degraded = computeDegraded(merged);

  // `lastCheckedAt` participates in the guard, so every observation made at a
  // new millisecond commits its timestamp. `forceNotify` covers repeated
  // observations that land in the same millisecond — a failure must still
  // reach the subscribers that re-arm the retry probe.
  const unchanged =
    merged.navigatorOnline === state.navigatorOnline &&
    merged.serverReachable === state.serverReachable &&
    merged.degraded === state.degraded &&
    merged.lastCheckedAt === state.lastCheckedAt;
  if (unchanged && !forceNotify) return;

  state = merged;
  // Notify over a snapshot: iterating the live Set means a listener that
  // unsubscribes (or subscribes) mid-fan-out changes who gets this event.
  [...listeners].forEach((listener) => listener());
}

export const connectivityStore = {
  getState: (): ConnectivityState => state,

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** navigator.onLine changed (window 'online'/'offline' events). */
  markNavigatorOnline(online: boolean): void {
    setState({ navigatorOnline: online });
  },

  /**
   * The server was observed reachable ('up') or unreachable ('down').
   * 'unknown' resets the model back to the cold-start state.
   */
  markServerReachable(reachable: ServerReachability): void {
    // A 'down' observation must always commit + notify (forceNotify): the
    // clock can tick within the same millisecond across a burst of failing
    // calls, and `useConnectivity` re-arms its expedited retry probe off
    // this notification. 'unknown' is the cold-start reset, so it clears the
    // stamp instead of pretending a fresh observation just happened.
    setState(
      {
        serverReachable: reachable,
        lastCheckedAt: reachable === 'unknown' ? null : Date.now(),
      },
      reachable === 'down',
    );
  },

  /** For tests — restores the cold-start state and drops listeners. */
  __reset(next: Partial<ConnectivityState> = {}): void {
    // Recompute `degraded` so a seeded fixture can never hand consumers a state
    // where `degraded` contradicts navigatorOnline/serverReachable.
    const merged: ConnectivityState = { ...initialState, ...next };
    merged.degraded = computeDegraded(merged);
    state = merged;
    listeners.clear();
  },
};

export type ConnectivityStore = typeof connectivityStore;

export default connectivityStore;
